<template>
  <form-button
    variant="custom"
    mode="default"
    class="add-to-calendar-button"
    :class="{ disabled: iCalError }"
    type="button"
    :full-width="true"
    :color-overrides="colorOverrides"
    :aria-label="$t('events.addToCalendarOptionAriaLabel', { calendarType: $t(`events.calendarType.${calendarType}`) })"
    @click.stop.prevent="handleAddToCalendarAction"
  >
    {{ $t(`events.calendarType.${calendarType}`) }}
  </form-button>
</template>

<script setup>
import { ref } from 'vue';
import dayjs from 'dayjs';
import {
  CALENDARS,
  validate,
  buildICalEvent,
  downloadToFile,
  buildOnlineCalendarUrl,
} from '../../../../../helpers/calendar.helper';
import FormButton from '../../../../global/Forms/FormButton.vue';
import { useEventsAnnouncer } from '../../../../../composables/useEventsAnnouncer.js';

defineOptions({
  name: 'AddToCalendar',
});

const props = defineProps({
  calendarType: {
    type: String,
    default: CALENDARS.Google,
    validator: validate(CALENDARS),
  },
  eventData: {
    type: Object,
    default: () => ({}),
  },
  colorOverrides: {
    type: Object,
    default: null,
  },
});

const { $t } = useNuxtApp();
const { announce } = useEventsAnnouncer();

const iCalError = ref(false);

function iCalDownload(content) {
  downloadToFile(content, `${props.eventData.title}.ics`);
  announce($t('events.calendarDownloadSuccess'));
}

function handleAddToCalendarAction() {
  if (iCalError.value) {
    return;
  }
  if (props.calendarType === CALENDARS.iCal) {
    const iCalResult = buildICalEvent(props.eventData);
    if (iCalResult.error?.errors.length > 0) {
      console.error(...iCalResult.error.errors);
      iCalError.value = true;
      return;
    }
    iCalDownload(iCalResult.value);
    return;
  }
  if (props.eventData.type === 'event') {
    window.open(buildOnlineCalendarUrl(props.calendarType, props.eventData), '_blank');
    return;
  }
  const description =
    props.eventData.home_team && props.eventData.away_team
      ? `${props.eventData.home_team} ${props.eventData.versus ? 'vs' : ''} ${props.eventData.away_team}`
      : '';

  const athleticsTitle =
    props.eventData.title ||
    `${props.eventData.home_team} ${props.eventData.versus ? 'vs' : ''} ${props.eventData.away_team}`;

  const athleticsData = {
    title: athleticsTitle,
    start_at: props.eventData.event_date,
    end_at: dayjs(props.eventData.event_date).add(30, 'minutes').format(),
    location: props.eventData?.location || '',
    details: props.eventData?.details || description,
    all_day: false,
  };
  window.open(buildOnlineCalendarUrl(props.calendarType, athleticsData), '_blank');
}
</script>

<style lang="scss" scoped>
@use '../../../../global/scss/color_vars';
.add-to-calendar-button {
  &:hover {
    color: #ffffff;
  }
  &.disabled {
    background-color: color_vars.$color-grey-10;
    color: color_vars.$color-grey-40;
  }
}
</style>
